import { useEffect } from "react";
import {
  amplifyProvider,
  localStorageProvider,
  APIProvider,
} from "../AppController";
import { parseJwt } from "../util/jwt";
import { Hub } from "aws-amplify";
import { isHomeHealthExternalUser } from "../constants/sso";

/**
 * Hook to sync auth from Web -> Android.
 *
 * Design:
 * - The WebView is the source of truth for SSO/2FA login.
 * - After login (or on app load if already logged in), the web app pushes tokens into Android
 *   via window.AndroidAuth.setAuthTokens(...).
 * - On explicit sign-out (Hub "signOut" event), we clear Android auth.
 * - We do NOT clear Android auth on session-read failures or "token looks expired" checks here,
 *   because those are often transient during SSO redirects/page load and can cause crash loops.
 *   Expiry/invalid-token handling is done on the Android side (e.g., 401 → reauth flow).
 */
export const useAndroidAuth = () => {
  useEffect(() => {
    if (typeof window === "undefined" || !window.AndroidAuth) return;

    let isSyncing = false;
    let lastPushedIdToken: string | null = null;
    const EXPIRY_BUFFER_MS = 2 * 60 * 1000; // 2 minutes

    const pushToAndroid = (tokens: {
      idToken: string;
      accessToken?: string | null;
      refreshToken?: string | null;
      expiresAtMs?: number;
    }) => {
      try {
        if (!window.AndroidAuth?.setAuthTokens) return;
        window.AndroidAuth.setAuthTokens(JSON.stringify(tokens));
      } catch (e) {
        console.error("❌ Failed to call AndroidAuth.setAuthTokens:", e);
      }
    };

    const clearAndroid = (reason: string) => {
      try {
        if (window.AndroidAuth?.clearAuthWithReason) {
          window.AndroidAuth.clearAuthWithReason(reason);
        } else if (window.AndroidAuth?.clearAuth) {
          window.AndroidAuth.clearAuth();
        }
      } catch {
        // ignore
      }
    };

    const isTokenValid = (idToken: string): boolean => {
      try {
        const tokenData = parseJwt(idToken);
        const expSec: number | undefined = tokenData?.exp;
        if (typeof expSec !== "number" || expSec <= 0) return true;
        const expiresAtMs = expSec * 1000;
        return expiresAtMs > Date.now() + EXPIRY_BUFFER_MS;
      } catch {
        return true;
      }
    };

    const tryPushTokens = async () => {
      if (isSyncing) return;
      isSyncing = true;
      try {
        // 1) Prefer localStorage tokens (SSO flows store them here)
        const idToken = localStorageProvider.get("idToken");
        const accessToken = localStorageProvider.get("accessToken");
        const refreshToken = localStorageProvider.get("refreshToken");

        if (idToken && isTokenValid(idToken)) {
          if (lastPushedIdToken === idToken) return;
          const tokenData = parseJwt(idToken);
          const expSec: number | undefined = tokenData?.exp;
          const expiresAtMs =
            typeof expSec === "number" && expSec > 0
              ? expSec * 1000
              : undefined;
          pushToAndroid({ idToken, accessToken, refreshToken, expiresAtMs });
          lastPushedIdToken = idToken;
          return;
        }

        // 2) Non-SSO logins: tokens may live only in Amplify session storage.
        const session = await amplifyProvider.auth.currentSession();
        const ampIdToken = session.getIdToken().getJwtToken();
        const ampAccessToken = session.getAccessToken().getJwtToken();
        const ampRefreshToken =
          (session as any)?.getRefreshToken?.()?.getToken?.() ?? null;

        if (!isTokenValid(ampIdToken)) {
          // Token is expired — but don't clear Android here. Let Android's own
          // 401 handling or next explicit action handle it.
          lastPushedIdToken = null;
          return;
        }

        if (lastPushedIdToken === ampIdToken) return;
        const tokenData = parseJwt(ampIdToken);
        const expSec: number | undefined = tokenData?.exp;
        const expiresAtMs =
          typeof expSec === "number" && expSec > 0 ? expSec * 1000 : undefined;

        pushToAndroid({
          idToken: ampIdToken,
          accessToken: ampAccessToken,
          refreshToken: ampRefreshToken,
          expiresAtMs,
        });
        lastPushedIdToken = ampIdToken;
      } catch {
        // Could not read session — this is normal during page load, SSO redirects,
        // or when the user hasn't logged in yet. Do NOT clear Android tokens here;
        // Android may already have valid tokens and clearing would cause a crash loop.
        // Clearing is handled explicitly via the "signOut" Hub event below.
      } finally {
        isSyncing = false;
      }
    };

    // Initial sync (app load / page navigation)
    tryPushTokens();

    // Re-sync on auth transitions without requiring a full page reload.
    const unlisten = Hub.listen("auth", ({ payload }) => {
      const event = (payload as any)?.event as string | undefined;
      if (
        event === "signIn" ||
        event === "tokenRefresh" ||
        event === "configured"
      ) {
        tryPushTokens();
      } else if (event === "signOut") {
        // Explicit web sign-out — this is the ONE place we clear Android from the web side.
        clearAndroid("web_logout");
        lastPushedIdToken = null;
      }
    });

    // Also sync when tab becomes active again (common after SSO redirects).
    const onFocus = () => tryPushTokens();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tryPushTokens();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      try {
        unlisten();
      } catch {
        // ignore
      }
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  // Poll GET /patient-centric-app-v2/ping every 3s when running inside the
  // Android overlay WebView and the user is a home_health_external user.
  //
  // Why /ping and not /health: the *only* useful side effect of this poll is
  // that APIProvider.GET calls AuthService.getAuthenticationToken() before the
  // request goes out, which (when the JWT is within its 5-min refresh buffer)
  // refreshes the SSO tokens and pushes the new bundle to native via
  // window.AndroidAuth.setAuthTokens(...). That side effect runs purely on the
  // URL prefix check in apiProvider.ts and is independent of which backend
  // route is hit. /ping is a no-DB endpoint, so we get the token-refresh
  // behavior at ~zero backend cost (vs. /health which hits MySQL + Redis +
  // alembic_version on every poll).
  useEffect(() => {
    const isAndroidWebView =
      typeof window !== "undefined" && !!window.AndroidAuth;

    if (!isAndroidWebView || !isHomeHealthExternalUser()) return;

    const intervalId = setInterval(() => {
      APIProvider.GET("/patient-centric-app-v2/ping").catch(() => {
        // Silently ignore — this is a keep-alive ping.
      });
    }, 3000);

    return () => clearInterval(intervalId);
  }, []);
};
